import { useMemo } from "react";
import { useProject } from "../../App/state";
import {
  Drawing,
  Scene,
  SceneScanType,
} from "../../components/SpatialConfigurationMode/types";
import {
  useFloorplanByIdQuery,
  useGetScenesUnderSpaceQuery,
} from "../../generated/types";

type UseFloorplanDataResult = {
  drawing?: Drawing;
  scenes: Scene[];
  loading: boolean;
};

export const useFloorplanData = (
  floorplanId?: string,
  rootSpaceId?: string,
): UseFloorplanDataResult => {
  const project = useProject();

  const { data: floorplanData, loading: floorplanLoading } =
    useFloorplanByIdQuery({
      variables: {
        tenant: project,
        id: floorplanId!,
      },
      skip: !floorplanId,
    });

  const { data: scenesData, loading: scenesLoading } =
    useGetScenesUnderSpaceQuery({
      variables: {
        tenant: project,
        spaceId: rootSpaceId!,
      },
      skip: !rootSpaceId,
    });

  const drawing = useMemo(() => {
    if (!floorplanData?.floorplans[0]) {
      return undefined;
    }
    const fp = floorplanData.floorplans[0];
    return {
      id: fp.id,
      url: fp.signedUrl,
      width: fp.width,
      height: fp.height,
      originX: fp.originX,
      originY: fp.originY,
      mmScaleFactor: fp.mmScaleFactor,
      bimOriginX: fp.bimOriginX,
      bimOriginY: fp.bimOriginY,
      bimOriginZ: fp.bimOriginZ,
      floorHeight: fp.floorHeight,
      angleToTrueNorth: fp.angleToTrueNorth,
      bimFloorModels: fp.bimFloorModels || [],
    };
  }, [floorplanData]);

  const scenes = useMemo(() => {
    if (!scenesData?.spacesByFilter[0]) {
      return [];
    }

    const rootSpace = scenesData.spacesByFilter[0];
    const allScenes = [
      ...rootSpace.scenes,
      ...rootSpace.descendantSpaces.flatMap((space) => space.scenes),
    ];

    return allScenes.map(
      (scene): Scene => ({
        id: scene.id,
        name: scene.name,
        path: [], // We don't need path for this use case
        x: scene.mmX || undefined,
        y: scene.mmY || undefined,
        zOffset: scene.mmZOffset,
        aliasSceneId: scene.aliasScene?.id,
        subjects: scene.subjects,
        parentId: rootSpace.id,
        scanType: SceneScanType.DEFAULT,
        guiIndex: scene.guiIndex,
      }),
    );
  }, [scenesData]);

  return {
    drawing,
    scenes,
    loading: floorplanLoading || scenesLoading,
  };
};
